// What a member may do with stock here (spec/07 §6.3 `inventory`, ADR-0040 §16), read once from `GET /v1/me/access`
// so every Stock screen offers the same actions. `inventory:supplies` admits a member — the technician — to receive,
// count, issue, adjust and edit **supply items only**; where it is the only reason an action is offered, the action is
// held to supplies (`suppliesOnly`). The server checks every request again (spec/13 §1).

import type { StockItem } from '@clavian/api-client';

import { useAccess } from '../../api/queries';

export const INVENTORY = {
  read: 'inventory:read',
  receive: 'inventory:receive',
  count: 'inventory:count',
  waste: 'inventory:waste',
  adjust: 'inventory:adjust',
  manage: 'inventory:manage',
  reports: 'inventory:reports',
  supplies: 'inventory:supplies',
} as const;

/** Turning stock on is a Food and drinks setting (`inventory.enabled`). */
export const SETTINGS_FNB = 'settings:fnb';

export interface StockAccess {
  readonly ready: boolean;
  readonly read: boolean;
  /** Offered at all — for every item, or (`…SuppliesOnly`) for supply items only. */
  readonly receive: boolean;
  readonly receiveSuppliesOnly: boolean;
  readonly count: boolean;
  readonly countSuppliesOnly: boolean;
  /** Waste, staff meals and supply issues. */
  readonly usage: boolean;
  readonly usageSuppliesOnly: boolean;
  readonly adjust: boolean;
  readonly adjustSuppliesOnly: boolean;
  /** Items and packs; with `manageSuppliesOnly`, supply items only. Recipes, suppliers and the copy need the full permission. */
  readonly items: boolean;
  readonly manageSuppliesOnly: boolean;
  readonly manage: boolean;
  /** The ₹ figures and the reports. */
  readonly reports: boolean;
  /** May turn stock on (`settings:fnb`). */
  readonly turnOn: boolean;
}

export function useStockAccess(venueId: string): StockAccess {
  const access = useAccess({ venueId });
  const ready = access.status === 'ready';
  const can = (permission: string) => ready && access.can(permission);
  const supplies = can(INVENTORY.supplies);
  const narrow = (permission: string) => ({ any: can(permission) || supplies, only: !can(permission) && supplies });
  const receive = narrow(INVENTORY.receive);
  const count = narrow(INVENTORY.count);
  const usage = narrow(INVENTORY.waste);
  const adjust = narrow(INVENTORY.adjust);
  const items = narrow(INVENTORY.manage);
  return {
    ready,
    read: can(INVENTORY.read),
    receive: receive.any,
    receiveSuppliesOnly: receive.only,
    count: count.any,
    countSuppliesOnly: count.only,
    usage: usage.any,
    usageSuppliesOnly: usage.only,
    adjust: adjust.any,
    adjustSuppliesOnly: adjust.only,
    items: items.any,
    manageSuppliesOnly: items.only,
    manage: can(INVENTORY.manage),
    reports: can(INVENTORY.reports),
    turnOn: can(SETTINGS_FNB),
  };
}

/** The picker's rule for an action held to supplies: a supply item, or anything when it is not held. */
export const suppliesRule = (only: boolean) => (only ? (item: Pick<StockItem, 'kind'>) => item.kind === 'supply' : undefined);
